"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction } from "next-safe-action/hooks";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Mail } from "lucide-react";
import Link from "next/link";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

import {
  investorApplicationSchema,
  type InvestorApplicationFormValues,
  SECTORS,
  INVESTOR_STAGES,
  GEOGRAPHIC_FOCUSES,
  DISCOVERY_SOURCES,
  PORTFOLIO_SIZE_OPTIONS,
} from "@/features/auth/types/application-schemas";
import { submitInvestorApplication } from "@/features/auth/actions/submit-application";

export function InvestorApplicationForm() {
  const [submitted, setSubmitted] = useState(false);
  const [showRequestSection, setShowRequestSection] = useState(false);

  const form = useForm<InvestorApplicationFormValues>({
    resolver: zodResolver(investorApplicationSchema),
    defaultValues: {
      name: "",
      email: "",
      inviteCode: "",
      firmName: "",
      roleTitle: "",
      investmentThesis: "",
      stageFocus: [],
      sectorFocus: [],
      portfolioSize: undefined,
      checkSizeMin: null,
      checkSizeMax: null,
      geographicFocus: [],
      discoverySource: undefined,
      termsAccepted: false,
    },
    mode: "onBlur",
  });

  const { execute, status } = useAction(submitInvestorApplication, {
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (args) => {
      const message =
        args.error.serverError ?? "Couldn't submit your application. Please try again.";
      toast.error(message);
    },
  });
  const isPending = status === "executing";

  const thesis = form.watch("investmentThesis");
  const thesisLen = thesis?.length ?? 0;

  function onSubmit(values: InvestorApplicationFormValues) {
    execute({
      name: values.name,
      email: values.email,
      inviteCode: values.inviteCode,
      firmName: values.firmName || undefined,
      roleTitle: values.roleTitle || undefined,
      investmentThesis: values.investmentThesis,
      stageFocus: values.stageFocus,
      sectorFocus: values.sectorFocus,
      portfolioSize: values.portfolioSize,
      checkSizeMin: values.checkSizeMin ?? undefined,
      checkSizeMax: values.checkSizeMax ?? undefined,
      geographicFocus: values.geographicFocus || undefined,
      discoverySource: values.discoverySource,
    });
  }

  if (submitted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="flex flex-col items-center gap-6 py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-display font-bold text-foreground">
              Application Submitted!
            </h2>
            <p className="text-muted-foreground max-w-sm">
              We review applications within 2-3 business days. We'll be in touch soon.
            </p>
          </div>
          <Link href="/">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="pb-6">
        <CardTitle className="text-2xl font-display">Apply as Capital Partner</CardTitle>
        <CardDescription>
          Join Purple Venture Studio as an Investor
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            {/* Personal / Firm Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Personal &amp; Firm Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane@firm.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="inviteCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invite Code *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. PVS-A3X9K2" {...field} />
                    </FormControl>
                    <FormMessage />
                    {!showRequestSection && (
                      <button
                        type="button"
                        onClick={() => setShowRequestSection(true)}
                        className="w-full mt-2 flex items-center justify-between gap-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/70 transition-all duration-200 px-4 py-3 group cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0 group-hover:bg-primary/25 transition-colors">
                            <Mail className="h-4 w-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-semibold text-primary">Don't have an invite code?</p>
                            <p className="text-xs text-muted-foreground">Request one and we'll send it to your email</p>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1.5 rounded-full group-hover:bg-primary/20 transition-colors shrink-0">
                          Request Code →
                        </span>
                      </button>
                    )}
                    {showRequestSection && (
                      <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-primary shrink-0" />
                          <p className="text-sm font-semibold text-primary">Request an Invite Code</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Fill in your name and email above, then click below to request a code. We'll review your request and send one to your inbox.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            const name = form.getValues("name");
                            const email = form.getValues("email");
                            if (!name || !email) {
                              toast.error("Please fill in your Full Name and Email first.");
                              return;
                            }
                            toast.success("Request submitted! We'll send your invite code shortly.");
                            setShowRequestSection(false);
                          }}
                          className="w-full mt-1 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                        >
                          Send Me an Invite Code
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowRequestSection(false)}
                          className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firmName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Firm Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Ventures" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="roleTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role / Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Partner, GP, Angel Investor…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Investment Profile */}
            <div className="space-y-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Investment Profile
              </h3>

              {/* Investment Thesis */}
              <FormField
                control={form.control}
                name="investmentThesis"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Investment Thesis *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe your investment thesis and the type of companies you invest in..."
                        className="resize-none min-h-[140px]"
                        {...field}
                      />
                    </FormControl>
                    <div className="flex justify-between items-center">
                      <FormMessage />
                      <span
                        className={`text-xs ml-auto ${
                          thesisLen < 200
                            ? "text-destructive"
                            : thesisLen > 1000
                              ? "text-destructive"
                              : "text-muted-foreground"
                        }`}
                      >
                        {thesisLen}/1000 characters
                      </span>
                    </div>
                  </FormItem>
                )}
              />

              {/* Stage Focus */}
              <FormField
                control={form.control}
                name="stageFocus"
                render={() => (
                  <FormItem>
                    <FormLabel>Stage Focus * (select at least one)</FormLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                      {INVESTOR_STAGES.map((stage) => (
                        <FormField
                          key={stage}
                          control={form.control}
                          name="stageFocus"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(stage)}
                                  onCheckedChange={(checked) => {
                                    const current = field.value ?? [];
                                    if (checked) {
                                      field.onChange([...current, stage]);
                                    } else {
                                      field.onChange(current.filter((v) => v !== stage));
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal cursor-pointer">
                                {stage}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Sector Focus */}
              <FormField
                control={form.control}
                name="sectorFocus"
                render={() => (
                  <FormItem>
                    <FormLabel>Sector Focus * (select at least one)</FormLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                      {SECTORS.map((sector) => (
                        <FormField
                          key={sector}
                          control={form.control}
                          name="sectorFocus"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(sector)}
                                  onCheckedChange={(checked) => {
                                    const current = field.value ?? [];
                                    if (checked) {
                                      field.onChange([...current, sector]);
                                    } else {
                                      field.onChange(current.filter((v) => v !== sector));
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal cursor-pointer">
                                {sector}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Portfolio Size */}
              <FormField
                control={form.control}
                name="portfolioSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Portfolio Size *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Number of portfolio companies" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PORTFOLIO_SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Check Size Range */}
              <div className="space-y-2">
                <p className="text-sm font-medium leading-none">Check Size Range (USD)</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="checkSizeMin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs text-muted-foreground">Minimum</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="e.g. 25000"
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="checkSizeMax"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs text-muted-foreground">Maximum</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="e.g. 500000"
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Geographic Focus */}
              <FormField
                control={form.control}
                name="geographicFocus"
                render={() => (
                  <FormItem>
                    <FormLabel>Geographic Focus</FormLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                      {GEOGRAPHIC_FOCUSES.map((geo) => (
                        <FormField
                          key={geo}
                          control={form.control}
                          name="geographicFocus"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(geo)}
                                  onCheckedChange={(checked) => {
                                    const current = field.value ?? [];
                                    if (checked) {
                                      field.onChange([...current, geo]);
                                    } else {
                                      field.onChange(current.filter((v) => v !== geo));
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal cursor-pointer">
                                {geo}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Discovery */}
            <FormField
              control={form.control}
              name="discoverySource"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How did you hear about Purple?</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DISCOVERY_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Terms */}
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal cursor-pointer">
                      I agree to Purple Venture Studio's Terms &amp; Conditions, including the no
                      off-platform solicitation policy.
                    </FormLabel>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full btn-premium text-white"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Spinner className="mr-2" />
                  Submitting...
                </>
              ) : (
                "Submit Application"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
